import gql from 'graphql-tag'
import { client, removeTypenames } from './api'
import moment from 'moment'
import { Account } from './integrations'
import { ExperimentationGroup } from './experimentationGroups'

export interface ExperimentType {
  type: string
  label: string
  parameters: ExperimentTypeParameter[]
  deprecated: boolean
}

export interface ExperimentTypeParameter {
  name: string
  label: string
  type: ExperimentTypeParameterType
  required: boolean
  choices: ExperimentTypeChoice[]
  note?: string
}

export interface ExperimentTypeChoice {
  label: string
  value: string
}

export type ExperimentTypeParameterType =
  | 'boolean'
  | 'number'
  | 'choice'
  | 'string'

export interface ExperimentParameter {
  name: string
  value: any
}

export interface Experiment {
  id: string
  tag: string
  type: string
  startDate: string
  endDate?: string
  allocation: number
  inventory: ExperimentInventory
  parameters: ExperimentParameter[]
}

export interface ExperimentInventory {
  account?: string
  domain?: string
  experimentGroup?: ExperimentationGroup
}

export const getExperimentsAndTypesQuery = gql`
  query {
    experimentTypes {
      type
      label
      deprecated
      parameters {
        name
        label
        type
        required
        note
        choices {
          label
          value
        }
      }
    }
    experiments {
      id
      tag
      type
      startDate
      endDate
      allocation
      inventory {
        ... on GraphQLExperimentInventoryAccount {
          __typename
          account
        }
        ... on GraphQLExperimentInventoryDomain {
          __typename
          account
          domain
        }
        ... on GraphQLExperimentInventoryGroup {
          __typename
          experimentGroup {
            id
            title
            accounts
            domains
          }
        }
      }
      parameters {
        name
        value
      }
    }
    experimentGroups {
      id
      title
    }
    allAccounts {
      account
      displayName
      domains
    }
  }
`

export interface SelectableExperimentationGroup {
  id: string
  title: string
}

export interface ExperimentsAndTypesQueryResponse {
  experiments: Experiment[]
  experimentTypes: ExperimentType[]
  experimentGroups: SelectableExperimentationGroup[]
  allAccounts: Account[]
}

export async function getExperimentsAndTypes() {
  return client.query({
    query: getExperimentsAndTypesQuery,
    variables: {},
    fetchPolicy: 'network-only',
  })
}

export const getExperimentOverviewQuery = gql`
  query {
    experimentOverview {
      domain
      experiments {
        id
        tag
      }
    }
  }
`

export interface ExperimentOverviewEntry {
  domain: string
  experiments: { id: string; tag: string }[]
}

export interface ExperimentOverviewQueryResponse {
  experimentOverview: ExperimentOverviewEntry[]
}

export interface ExperimentInventoryInput {
  account?: string
  domain?: string
  experimentGroupId?: string
}

export interface ExperimentInput {
  type: string
  startDate: string
  endDate?: string
  allocation: number
  inventory: ExperimentInventoryInput
  parameters: ExperimentParameter[]
}

export async function createExperiment(experiment: ExperimentInput) {
  removeTypenames(experiment)
  return client
    .mutate({
      mutation: gql`
        mutation (
          $type: GraphQLExperimentTypes!
          $inventory: GraphQLExperimentInventoryInput!
          $allocation: Int!
          $startDate: String!
          $endDate: String
          $parameters: [GraphQLExperimentParameterInput!]!
        ) {
          experiments {
            create(
              type: $type
              inventory: $inventory
              allocation: $allocation
              startDate: $startDate
              endDate: $endDate
              parameters: $parameters
            ) {
              id
            }
          }
        }
      `,
      variables: {
        type: experiment.type,
        inventory: experiment.inventory,
        allocation: experiment.allocation,
        startDate: experiment.startDate,
        endDate: normalizeEndDate(experiment.endDate),
        parameters: experiment.parameters,
      },
    })
    .catch(() => {
      throw new Error('Cannot create experiment')
    })
}

export async function setEndDate({
  id,
  endDate,
}: {
  id: string
  endDate: string
}) {
  return client
    .mutate({
      mutation: gql`
        mutation ($id: String!, $endDate: String) {
          experiments {
            setExpirationDate(id: $id, endDate: $endDate) {
              id
            }
          }
        }
      `,
      variables: {
        id,
        endDate: normalizeEndDate(endDate),
      },
    })
    .catch(() => {
      throw new Error('Cannot set end date of experiment')
    })
}

// For today, we have to use current time or the API will reject it as a past end date. For other days,
// we can stick to the start of the day.
function normalizeEndDate(endDate: string | undefined) {
  if (!endDate) {
    return undefined
  }
  const momentEndDate = moment(endDate)
  if (momentEndDate.isSame(moment(), 'day')) {
    return moment().toISOString()
  }
  return momentEndDate.utc().startOf('day').toISOString()
}

export function isEnded(experiment: Experiment) {
  return experiment.endDate && moment().isAfter(experiment.endDate)
}
